import { resolveDashboardSteps } from "../tour/resolveDashboardSteps";
import { resolveSettingsTourSteps } from "../tour/resolveSettingsTourSteps";
import { TERRITORIES_TOUR_STEPS } from "../tour/steps/territories.steps";
import { LEADS_MAP_TOUR_STEPS } from "../tour/steps/leads-map.steps";
import { LEADS_MOBILE_MAP_TOUR_STEPS } from "../tour/steps/leads-mobile-map.steps";
import { SEARCH_LEADS_TOUR_STEPS } from "../tour/steps/search-leads.steps";
import { TEAM_CHAT_TOUR_STEPS } from "../tour/steps/team-chat.steps";
import { PRODUCTS_SERVICES_TOUR_STEPS } from "../tour/steps/products-services.steps";
import { TERRITORY_DETAIL_TOUR_STEPS } from "../tour/steps/territory-detail.steps";
import { LEAD_DETAIL_TOUR_STEPS } from "../tour/steps/lead-detail.steps";
import { EDIT_USER_TOUR_STEPS } from "../tour/steps/edit-user.steps";
import {
  SALES_PIPELINE_TOUR_STEPS,
  MESSAGE_CENTER_TOUR_STEPS,
  FOLLOW_UP_INTEL_TOUR_STEPS,
  CONVERSATIONS_TOUR_STEPS,
  REP_POSITIONS_TOUR_STEPS,
  CHANGELOG_TOUR_STEPS,
  COMPANY_PROFILE_TOUR_STEPS,
  ROLES_TOUR_STEPS,
  USERS_TOUR_STEPS,
  REPORTS_TOUR_STEPS,
  SUBSCRIPTION_TOUR_STEPS,
  INTEGRATIONS_TOUR_STEPS,
  ACTION_LOG_TOUR_STEPS,
} from "../tour/steps/sidebar-pages.steps";
import {
  getNarrationForRoute,
  resolveGuidedTourRoute,
  canAccessRoleSettings,
} from "./narrationScripts";

const PAGE_LABELS = {
  "/dashboard": "Dashboard",
  "/sales-pipeline": "Sales pipeline",
  "/territories": "Territories",
  "/leads": "Leads Map",
  "/follow-up-intelligence": "Follow-Up Intel",
  "/leads/search": "Search Leads",
  "/message-center": "Message Center",
  "/conversations": "Live SMS",
  "/team-chat": "Team Chat",
  "/rep-positions": "Rep Positions",
  "/changelog": "What's New",
  "/settings/company-profile": "Company Profile",
  "/settings/products-services": "Products & Services",
  "/settings/roles": "Roles & Groups",
  "/users": "Users",
  "/settings/reports": "Reports",
  "/settings/subscription": "Subscriptions",
  "/settings/integrations": "Integrations",
  "/settings/action-log": "Action Log",
  "/territory/add/lead": "Territory leads",
  "/leads/detail": "Lead detail",
  "/users/edit": "Edit user",
};

const LEADERSHIP_ROLES = ["Admin", "Sales Director", "Manager"];

function resolveSteps(factory, role, ctx = {}) {
  if (!factory) return null;
  try {
    const steps =
      typeof factory === "function" ? factory(role, ctx) : factory;
    return Array.isArray(steps) && steps.length > 0 ? steps : null;
  } catch {
    return null;
  }
}

function getPageTourSteps(routeKey, role, ctx) {
  if (!routeKey || !role) return null;

  const map = {
    "/dashboard": null,
    "/sales-pipeline": SALES_PIPELINE_TOUR_STEPS,
    "/territories": TERRITORIES_TOUR_STEPS,
    "/leads": ctx.isMobileLayout ? LEADS_MOBILE_MAP_TOUR_STEPS : LEADS_MAP_TOUR_STEPS,
    "/follow-up-intelligence": FOLLOW_UP_INTEL_TOUR_STEPS,
    "/leads/search": SEARCH_LEADS_TOUR_STEPS,
    "/message-center": MESSAGE_CENTER_TOUR_STEPS,
    "/conversations": CONVERSATIONS_TOUR_STEPS,
    "/team-chat": () => TEAM_CHAT_TOUR_STEPS(),
    "/rep-positions": REP_POSITIONS_TOUR_STEPS,
    "/changelog": CHANGELOG_TOUR_STEPS,
    "/settings/company-profile": COMPANY_PROFILE_TOUR_STEPS,
    "/settings/products-services": PRODUCTS_SERVICES_TOUR_STEPS,
    "/settings/roles": ROLES_TOUR_STEPS,
    "/users": USERS_TOUR_STEPS,
    "/settings/reports": REPORTS_TOUR_STEPS,
    "/settings/subscription": SUBSCRIPTION_TOUR_STEPS,
    "/settings/integrations": INTEGRATIONS_TOUR_STEPS,
    "/settings/action-log": ACTION_LOG_TOUR_STEPS,
    "/territory/add/lead": TERRITORY_DETAIL_TOUR_STEPS,
    "/leads/detail": LEAD_DETAIL_TOUR_STEPS,
    "/users/edit": EDIT_USER_TOUR_STEPS,
  };

  const factory = map[routeKey];
  if (!factory) return null;
  return resolveSteps(factory, role, ctx);
}

function getPageLabel(routeKey) {
  if (routeKey && PAGE_LABELS[routeKey]) return PAGE_LABELS[routeKey];
  return "This page";
}

function isInsideSettingsRoute(routeKey) {
  return Boolean(
    routeKey?.startsWith("/settings/") || routeKey === "/users"
  );
}

/**
 * Help available on the current route for Top bar menu and replay.
 */
export function resolvePageHelp(
  pathname,
  { role, user, isMobileLayout = false } = {}
) {
  const routeKey = resolveGuidedTourRoute(pathname);
  const pageLabel = getPageLabel(routeKey);
  const narrationScript = getNarrationForRoute(pathname, { role, user });
  const roleSettingsAccess = canAccessRoleSettings(user, role);

  const tourCtx = {
    isMobileLayout,
    canAccessRoleSettings: roleSettingsAccess,
  };
  const guidedTourSteps = getPageTourSteps(routeKey, role, tourCtx);
  const guidedTour = guidedTourSteps
    ? {
        label: `Tour ${pageLabel}`,
        steps: guidedTourSteps,
      }
    : null;

  const mainNavSteps = role
    ? resolveDashboardSteps(role, {
        canAccessRoleSettings: roleSettingsAccess,
      })
    : [];
  const mainNavTour =
    mainNavSteps.length > 0
      ? {
          label: "Replay app overview",
          steps: mainNavSteps,
        }
      : null;

  const settingsSteps =
    role && LEADERSHIP_ROLES.includes(role)
      ? resolveSettingsTourSteps(role, {
          canAccessRoleSettings: roleSettingsAccess,
        })
      : null;

  const settingsTour =
    settingsSteps?.length > 0 && !isInsideSettingsRoute(routeKey)
      ? {
          label: "Tour settings menu",
          steps: settingsSteps,
          needsSettingsExpanded: true,
        }
      : null;

  const hasAnyAction =
    Boolean(narrationScript) ||
    Boolean(guidedTour?.steps?.length) ||
    Boolean(mainNavTour?.steps?.length) ||
    Boolean(settingsTour?.steps?.length);

  return {
    routeKey,
    pageLabel,
    narrationScript,
    guidedTour,
    mainNavTour:
      mainNavTour?.steps?.length > 0 ? mainNavTour : null,
    settingsTour,
    hasAnyAction,
  };
}
